import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useState, useEffect } from "react";
import api from "@/lib/api";
import {
    LayoutDashboard, Users, BookOpen, Layers, FileText, ListChecks,
    Award, Megaphone, Bell, Search, Sun, Moon, LogOut, GraduationCap,
    Menu, X, Settings, ChevronDown, FolderOpen, CalendarCheck, Wallet,
    MessageCircleQuestion, TriangleAlert,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";

const NAV = {
    ADMIN: [
        { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
        { to: "/admin/students", label: "Students", icon: Users },
        { to: "/admin/faculty", label: "Faculty", icon: GraduationCap },
        { to: "/admin/batches", label: "Batches", icon: Layers },
        { to: "/admin/courses", label: "Courses", icon: BookOpen },
        { to: "/admin/assignments", label: "Assignments", icon: FileText },
        { to: "/admin/quizzes", label: "Quizzes", icon: ListChecks },
        { to: "/admin/attendance", label: "Attendance", icon: CalendarCheck },
        { to: "/admin/fees", label: "Fees", icon: Wallet },
        { to: "/admin/doubts", label: "Doubts", icon: MessageCircleQuestion },
        { to: "/admin/certificates", label: "Certificates", icon: Award },
        { to: "/resources", label: "Resources", icon: FolderOpen },
        { to: "/admin/announcements", label: "Announcements", icon: Megaphone },
    ],
    FACULTY: [
        { to: "/faculty/dashboard", label: "Dashboard", icon: LayoutDashboard },
        { to: "/faculty/submissions", label: "Submissions", icon: FileText },
        { to: "/faculty/attendance", label: "Attendance", icon: CalendarCheck },
        // Same report screen as ADMIN; the backend scopes it to this faculty's batches.
        { to: "/admin/attendance", label: "Attendance report", icon: ListChecks },
        // Same queue as ADMIN; the backend scopes doubts to this faculty's batches.
        { to: "/admin/doubts", label: "Doubts", icon: MessageCircleQuestion },
        { to: "/faculty/batches", label: "My Batches", icon: Layers },
        { to: "/faculty/courses", label: "Courses", icon: BookOpen },
        { to: "/resources", label: "Resources", icon: FolderOpen },
        { to: "/faculty/announcements", label: "Announcements", icon: Megaphone },
    ],
    STUDENT: [
        { to: "/student/dashboard", label: "Dashboard", icon: LayoutDashboard },
        { to: "/student/courses", label: "My Courses", icon: BookOpen },
        { to: "/student/assignments", label: "Assignments", icon: FileText },
        { to: "/student/quizzes", label: "Quizzes", icon: ListChecks },
        { to: "/student/attendance", label: "Attendance", icon: CalendarCheck },
        { to: "/student/fees", label: "My Fees", icon: Wallet },
        { to: "/student/doubts", label: "Doubts", icon: MessageCircleQuestion },
        { to: "/student/certificates", label: "Certificates", icon: Award },
        { to: "/resources", label: "Resources", icon: FolderOpen },
        { to: "/student/announcements", label: "Announcements", icon: Megaphone },
    ],
};

function NavList({ items, onNavigate }) {
    return (
        <nav className="flex flex-col gap-1 p-3">
            {items.map(({ to, label, icon: Icon }) => (
                <NavLink
                    key={to} to={to} onClick={onNavigate}
                    data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                            isActive
                                ? "bg-[#F0EFFF] dark:bg-[#1C1A2E] text-[#726AF0]"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                        }`
                    }
                >
                    <Icon className="h-4 w-4" />
                    <span>{label}</span>
                </NavLink>
            ))}
        </nav>
    );
}

function BrandLogo() {
    return (
        <Link to="/" className="flex items-center gap-2.5 px-5 py-5" data-testid="brand-logo">
            <div className="h-9 w-9 rounded-xl bg-brand-gradient flex items-center justify-center shadow-mx-md">
                <span className="font-display text-white text-lg">M</span>
            </div>
            <div className="leading-tight">
                <div className="font-display text-lg">MX CAMPUS</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Learning Hub</div>
            </div>
        </Link>
    );
}

function NotificationsBell() {
    const [open, setOpen] = useState(false);
    const [data, setData] = useState({ items: [], unread: 0 });
    const load = async () => {
        try { const { data } = await api.get("/notifications"); setData(data); } catch (_e) {}
    };
    useEffect(() => {
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, []);
    const markRead = async (id) => { await api.post(`/notifications/${id}/read`); load(); };
    const markAll = async () => { await api.post(`/notifications/read-all`); load(); };
    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-bell">
                    <Bell className="h-5 w-5" />
                    {data.unread > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-brand-gradient text-white text-[10px] font-bold flex items-center justify-center" data-testid="notifications-unread-count">
                            {data.unread}
                        </span>
                    )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
                <div className="flex items-center justify-between px-3 py-2">
                    <DropdownMenuLabel className="p-0">Notifications</DropdownMenuLabel>
                    <button onClick={markAll} className="text-xs text-[#726AF0] hover:underline" data-testid="mark-all-read">Mark all read</button>
                </div>
                <DropdownMenuSeparator />
                <div className="max-h-80 overflow-auto scrollbar-thin">
                    {data.items.length === 0 ? (
                        <div className="py-8 text-center text-sm text-muted-foreground">No notifications</div>
                    ) : data.items.slice(0, 20).map((n) => (
                        <button
                            key={n.id}
                            onClick={() => markRead(n.id)}
                            className={`w-full text-left px-3 py-2.5 hover:bg-muted ${n.read ? "" : "bg-[#F0EFFF]/40 dark:bg-[#1C1A2E]/40"}`}
                            data-testid={`notification-${n.id}`}
                        >
                            <div className="text-sm font-semibold">{n.title}</div>
                            <div className="text-xs text-muted-foreground line-clamp-2">{n.body}</div>
                        </button>
                    ))}
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function GlobalSearch() {
    const [q, setQ] = useState("");
    const [results, setResults] = useState(null);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (!q || q.length < 2) { setResults(null); return; }
        const t = setTimeout(async () => {
            try { const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`); setResults(data); setOpen(true); }
            catch (_e) {}
        }, 250);
        return () => clearTimeout(t);
    }, [q]);

    const goto = (path) => { setOpen(false); setQ(""); navigate(path); };
    const total = results ? results.courses.length + results.lessons.length + results.resources.length + results.assignments.length : 0;
    return (
        <div className="relative w-full max-w-md hidden md:block">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search courses, lessons, materials…"
                className="pl-9 h-10 bg-muted/40 border-border rounded-xl"
                data-testid="global-search-input"
                onFocus={() => results && setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
            />
            {open && results && total > 0 && (
                <div className="absolute z-50 mt-2 w-full bg-popover border border-border rounded-xl shadow-mx-lg p-2 max-h-96 overflow-auto scrollbar-thin" data-testid="search-results">
                    {[
                        ["Courses", results.courses, "/admin/courses"],
                        ["Lessons", results.lessons, ""],
                        ["Materials", results.resources, ""],
                        ["Assignments", results.assignments, ""],
                    ].map(([label, items]) => items.length > 0 && (
                        <div key={label} className="mb-2 last:mb-0">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground px-2 py-1">{label}</div>
                            {items.map((it) => (
                                <button key={it.id} className="block w-full text-left px-2 py-1.5 rounded-lg hover:bg-muted text-sm" onClick={() => goto("/")}>
                                    {it.title}
                                </button>
                            ))}
                        </div>
                    ))}
                </div>
            )}
            {open && results && total === 0 && (
                <div className="absolute z-50 mt-2 w-full bg-popover border border-border rounded-xl shadow-mx-md p-4 text-sm text-muted-foreground">No results found</div>
            )}
        </div>
    );
}

function UserMenu() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    if (!user) return null;
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-xl hover:bg-muted transition-colors" data-testid="user-menu-trigger">
                    <div className="h-8 w-8 rounded-full bg-brand-gradient flex items-center justify-center text-white font-bold text-sm">
                        {user.name?.[0] || "?"}
                    </div>
                    <div className="hidden md:block text-left leading-tight">
                        <div className="text-sm font-semibold">{user.name}</div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{user.role}</div>
                    </div>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                    <div className="text-sm font-semibold">{user.name}</div>
                    <div className="text-xs text-muted-foreground">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                    <Settings className="h-4 w-4 mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="menu-logout">
                    <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function MustResetBanner() {
    const navigate = useNavigate();
    const location = useLocation();
    if (location.pathname === "/settings") return null;
    return (
        <div className="bg-amber-500/10 border-b border-amber-500/30 px-4 lg:px-6 py-2.5 flex items-center gap-3 text-sm">
            <TriangleAlert className="h-4 w-4 text-amber-500 shrink-0" />
            <span className="text-amber-700 dark:text-amber-400 flex-1">
                You're using a temporary password. Please set a permanent one now.
            </span>
            <button
                onClick={() => navigate("/settings")}
                className="text-amber-700 dark:text-amber-400 font-semibold underline underline-offset-2 hover:text-amber-900 dark:hover:text-amber-200 shrink-0"
            >
                Change password
            </button>
        </div>
    );
}

export default function AppShell({ children }) {
    const { user } = useAuth();
    const { theme, toggle } = useTheme();
    const [drawer, setDrawer] = useState(false);
    if (!user) return null;
    const items = NAV[user.role] || [];

    return (
        <div className="min-h-screen flex bg-background">
            {/* Sidebar - desktop */}
            <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-border bg-card sticky top-0 h-screen">
                <BrandLogo />
                <div className="px-5 mb-2">
                    <Badge className="bg-[#F0EFFF] dark:bg-[#1C1A2E] text-[#726AF0] hover:bg-[#F0EFFF] border-0 font-semibold">
                        {user.role}
                    </Badge>
                </div>
                <div className="flex-1 overflow-auto scrollbar-thin">
                    <NavList items={items} />
                </div>
                <div className="p-3 border-t border-border">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground px-3 mb-2">Signed in as</div>
                    <div className="px-3 pb-1 text-sm font-semibold truncate">{user.name}</div>
                    <div className="px-3 pb-3 text-xs text-muted-foreground truncate">{user.email}</div>
                </div>
            </aside>

            <div className="flex-1 min-w-0 flex flex-col">
                {/* Topbar */}
                <header className="sticky top-0 z-30 h-16 px-4 lg:px-6 border-b border-border bg-card/90 backdrop-blur flex items-center gap-4">
                    <Sheet open={drawer} onOpenChange={setDrawer}>
                        <SheetTrigger asChild>
                            <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-toggle">
                                <Menu className="h-5 w-5" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="left" className="w-72 p-0">
                            <BrandLogo />
                            <NavList items={items} onNavigate={() => setDrawer(false)} />
                        </SheetContent>
                    </Sheet>

                    <GlobalSearch />

                    <div className="ml-auto flex items-center gap-1.5">
                        <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" aria-label="Toggle theme">
                            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                        </Button>
                        <NotificationsBell />
                        <UserMenu />
                    </div>
                </header>

                {user.must_reset_password && <MustResetBanner />}

                <main className="flex-1 p-4 lg:p-8 max-w-[1400px] w-full mx-auto animate-fade-in">
                    {children}
                </main>
            </div>
        </div>
    );
}
